import { toast } from "@/components/ui";
import { TRACKERS } from "@/constants/Trackers";
import type { ProgramEnrollment } from "@/features/Charting/RoomOptions/network/useEncounterTransitions";
import { trackEvent } from "@/lib/tracking";
import type { EncounterProgram } from "@curai/js-fhir-tools";
import { zodResolver } from "@hookform/resolvers/zod";
import { type FormEvent, useMemo, useRef, useState } from "react";
import { type Resolver, type ResolverResult, useForm } from "react-hook-form";
import { BillingCodesStep } from "./BillingCodesStep/BillingCodesStep";
import { BillingSummaryStep } from "./BillingSummaryStep/BillingSummaryStep";
import { CPTCodesStep } from "./CPTCodesStep/CPTCodesStep";
import { CarePlanPreviewStep } from "./CarePlanPreviewStep/CarePlanPreviewStep";
import { DiagnosisCharting } from "./DiagnosisChartingStep/DiagnosisCharting";
import { DxBillingCodesStep } from "./DxBillingCodesStep/DxBillingCodesStep";
import { useAddBillingCodes } from "./DxBillingCodesStep/network";
import { ProgramEnrollmentStep } from "./ProgramEnrollmentStep/ProgramEnrollmentStep";
import { buildProgramEnrollment } from "./ProgramEnrollmentStep/programEnrollment";
import { RequiresLiveVisitStep } from "./RequiresLiveVisitStep/RequiresLiveVisitStep";
import { VisitClosureFooter } from "./VisitClosureFooter";
import { VisitDurationStep } from "./VisitDurationStep/VisitDurationStep";
import { useBatchAddDxFromVisitDx, useClaimSubmit } from "./network";
import type { EncounterCodingResponse } from "./network";
import {
  type VisitClosureFormData,
  type VisitClosureFormInput,
  getVisitClosureFormSchema,
} from "./schemas";
import { useVisitClosureSteps } from "./steps";
import type { VisitClosureDialogLaunch } from "./visitClosureDialogContext";

const rejectAsyncVisitResolver: Resolver<
  VisitClosureFormInput,
  unknown,
  VisitClosureFormData
> = (values) => {
  if (!values.rejectAsyncVisit.cancelReasonCode) {
    const result: ResolverResult<VisitClosureFormInput, VisitClosureFormData> =
      {
        values: {},
        errors: {
          rejectAsyncVisit: {
            cancelReasonCode: {
              type: "required",
              message: "A cancellation reason is required",
            },
          },
        },
      };

    return result;
  }

  const result: ResolverResult<VisitClosureFormInput, VisitClosureFormData> = {
    values,
    errors: {},
  };

  return result;
};

const passThroughResolver: Resolver<
  VisitClosureFormInput,
  unknown,
  VisitClosureFormData
> = (values) => ({ values, errors: {} });

interface VisitClosureFlowProps {
  patientId: string;
  userId: number;
  encounterId: string;
  encounterCodingData: EncounterCodingResponse;
  encounterProgram: EncounterProgram | null;
  setDialogOpen: (open: boolean) => void;
  launch: VisitClosureDialogLaunch;
  step: number;
  setStep: (step: number) => void;
}

export const VisitClosureFlow = ({
  patientId,
  userId,
  encounterId,
  encounterCodingData,
  encounterProgram,
  setDialogOpen,
  launch,
  step,
  setStep,
}: VisitClosureFlowProps) => {
  const visitCompleted =
    launch.mode === "billing" ? launch.visitCompleted : false;
  const pendingEnrollmentRef = useRef<ProgramEnrollment | undefined>(undefined);
  const addedDiagnosisIdsRef = useRef({
    encounterId,
    ids: new Set<string>(),
  });
  const [selectableDiagnosisCount, setSelectableDiagnosisCount] = useState(0);
  const [isDiagnosisLoading, setIsDiagnosisLoading] = useState(true);
  const [isCarePlanPreviewReady, setIsCarePlanPreviewReady] = useState(false);
  const visitDiagnosesCount = encounterCodingData.visitDiagnoses?.length ?? 0;
  const { requiresDiagnosisBillingStep, requiresEnrollmentDecision, steps } =
    useVisitClosureSteps({
      visitDiagnosesCount,
      hasChildEncounters:
        (encounterCodingData.childEncounters?.length ?? 0) > 0,
      encounterProgram,
      visitCompleted,
      mode: launch.mode,
    });
  const currentStep = steps[step];
  const formSchema = useMemo(
    () =>
      getVisitClosureFormSchema({
        requiresDxBillingCodes: requiresDiagnosisBillingStep,
        requiresLegacyIcd10: !requiresDiagnosisBillingStep,
        requiresEnrollmentDecision,
      }),
    [requiresDiagnosisBillingStep, requiresEnrollmentDecision],
  );
  const { mutateAsync: submitClaim, isPending: isSubmittingClaim } =
    useClaimSubmit(
      patientId,
      encounterId,
      () => {
        // Tracked here rather than on the submit button so it only fires once
        // the claim POST has resolved. Must stay above setDialogOpen(false),
        // which fires CLOSE_BILLING_MODAL, so a submit is always bracketed by
        // the open/close pair.
        trackEvent(TRACKERS.SUBMIT_BILLING_CLAIM);
        setDialogOpen(false);
        if (launch.mode === "billing") {
          launch.onSuccess(pendingEnrollmentRef.current);
        }
      },
      (error: Error) => {
        toast.error(`Failed to submit claim: ${error.message}`);
      },
    );
  const { mutateAsync: batchAddDxFromVisitDx, isPending: isBatchAddDxPending } =
    useBatchAddDxFromVisitDx({ patientId });
  const { mutateAsync: addBillingCodes, isPending: isAddBillingCodesPending } =
    useAddBillingCodes({
      patientId,
      encounterId,
      onError: () => {
        toast.error("Failed to add billing codes to the encounter.");
      },
    });
  const [isRejectingAsyncVisit, setIsRejectingAsyncVisit] = useState(false);
  const [isSendingCarePlan, setIsSendingCarePlan] = useState(false);
  const isSubmitting =
    isSubmittingClaim ||
    isBatchAddDxPending ||
    isAddBillingCodesPending ||
    isRejectingAsyncVisit ||
    isSendingCarePlan;

  const handleSendRequiresLiveVisit = async ({
    cancelReasonCode,
    cancelReasonText,
  }: {
    cancelReasonCode: string;
    cancelReasonText?: string;
  }) => {
    if (launch.mode !== "rejectAsyncVisit") {
      return;
    }

    setIsRejectingAsyncVisit(true);
    try {
      await launch.onRejectAsyncVisit({
        cancelReasonCode,
        cancelReasonText,
      });
      setDialogOpen(false);
    } catch {
      // Error toast is shown by the mutation's onError; keep the modal open
      // so the user can retry without re-entering their reason.
    } finally {
      setIsRejectingAsyncVisit(false);
    }
  };

  const addSelectedVisitDiagnoses = async (
    selectedDiagnosisIds: string[] | undefined,
  ) => {
    if (addedDiagnosisIdsRef.current.encounterId !== encounterId) {
      addedDiagnosisIdsRef.current = {
        encounterId,
        ids: new Set<string>(),
      };
    }

    const diagnosisIdsToAdd = (selectedDiagnosisIds ?? []).filter(
      (conditionId) => !addedDiagnosisIdsRef.current.ids.has(conditionId),
    );

    if (visitDiagnosesCount === 0 || diagnosisIdsToAdd.length === 0) {
      return true;
    }

    try {
      await batchAddDxFromVisitDx({
        encounterDiagnosisConditionIds: diagnosisIdsToAdd,
      });
      for (const conditionId of diagnosisIdsToAdd) {
        addedDiagnosisIdsRef.current.ids.add(conditionId);
      }

      return true;
    } catch {
      toast.error("Failed to add selected diagnoses to the medical record.");

      return false;
    }
  };

  const onSubmit = async (data: VisitClosureFormData) => {
    if (launch.mode === "rejectAsyncVisit") {
      const didAddSelectedVisitDiagnoses = await addSelectedVisitDiagnoses(
        data.selectedDiagnosisIds,
      );
      if (!didAddSelectedVisitDiagnoses) {
        return;
      }

      const trimmedCancelReasonText =
        data.rejectAsyncVisit.cancelReasonText.trim();
      await handleSendRequiresLiveVisit({
        cancelReasonCode: data.rejectAsyncVisit.cancelReasonCode,
        cancelReasonText: trimmedCancelReasonText || undefined,
      });
      return;
    }

    if (launch.mode === "billingless") {
      const didAddSelectedVisitDiagnoses = await addSelectedVisitDiagnoses(
        data.selectedDiagnosisIds,
      );
      if (!didAddSelectedVisitDiagnoses) {
        return;
      }

      setIsSendingCarePlan(true);
      try {
        await launch.onSendCarePlan();
        setDialogOpen(false);
      } catch {
        // Error feedback is shown by the room action; keep the dialog open so
        // the clinician can retry without adding the diagnoses again.
      } finally {
        setIsSendingCarePlan(false);
      }
      return;
    }

    const patientType = data.encounterTimeData.patientType;
    if (!patientType) {
      return;
    }

    // Claim submission payload
    const [primaryVisitBillingCode, ...additionalVisitBillingCodes] =
      data.dxBillingCodes;
    const payload = {
      ...(primaryVisitBillingCode
        ? {
            additionalBillingCodes: additionalVisitBillingCodes.map((code) => ({
              coding: [
                {
                  code: code.code,
                  system: code.system,
                  display: code.display,
                  version: code.version,
                },
              ],
              text: code.display,
            })),
          }
        : {}),
      motherEncounterId: encounterId,
      billableChildEncounters:
        visitDiagnosesCount === 0
          ? data.icd10.childEncounters.map((childEncounter) => {
              return {
                childEncounterId: childEncounter.encounterId,
                icd10Codes: childEncounter.icd10Codes.map((code) => {
                  return {
                    code: code.code,
                    system: code.system,
                    display: code.display,
                    version: code.version,
                  };
                }),
              };
            })
          : [],
      overriddenEncounterTime: data.encounterTimeData.chartingTimeSpent,
      overriddenAvCallTime: data.encounterTimeData.avCallTimeSpent,
      postEncounterActivities:
        data.encounterTimeData.postEncounterActivities.map((activity) => {
          return {
            activityType: activity.activityType,
            time: activity.time,
          };
        }),
      primaryIcd10Code: primaryVisitBillingCode
        ? {
            coding: [
              {
                code: primaryVisitBillingCode.code,
                system: primaryVisitBillingCode.system,
                display: primaryVisitBillingCode.display,
                version: primaryVisitBillingCode.version,
              },
            ],
            text: primaryVisitBillingCode.display,
          }
        : {
            coding: [
              {
                code: data.icd10.primaryIcd10Code.code,
                system: data.icd10.primaryIcd10Code.system,
                display: data.icd10.primaryIcd10Code.display,
                version: data.icd10.primaryIcd10Code.version,
              },
            ],
            text: data.icd10.primaryIcd10Code.display,
          },
      cptCode: data.cptCode,
      billType: data.encounterTimeData.billType,
      visitType: data.encounterTimeData.visitType,
      finalized: visitCompleted,
      patient: {
        id: patientId,
        userId,
        // TODO: remove this once backend is changed
        partnerUid: 0,
        // TODO: remove this once backend is changed
        isDependent: false,
        type: patientType,
      },
    };

    const didAddSelectedVisitDiagnoses = await addSelectedVisitDiagnoses(
      data.selectedDiagnosisIds,
    );
    if (!didAddSelectedVisitDiagnoses) {
      return;
    }

    if (primaryVisitBillingCode) {
      try {
        await addBillingCodes({
          billingCodes: [
            {
              code: primaryVisitBillingCode.code,
              display: primaryVisitBillingCode.display,
            },
            ...additionalVisitBillingCodes.map(({ code, display }) => ({
              code,
              display,
            })),
          ],
        });
      } catch {
        return;
      }
    }

    pendingEnrollmentRef.current = requiresEnrollmentDecision
      ? buildProgramEnrollment(data)
      : undefined;

    try {
      await submitClaim(payload);
    } catch {
      // Do nothing, the error is handled by the useClaimSubmit hook. DO NOT BLOCK CHART CLOSURE ON ERROR.
    }
  };

  const form = useForm<VisitClosureFormInput, unknown, VisitClosureFormData>({
    resolver:
      launch.mode === "rejectAsyncVisit"
        ? rejectAsyncVisitResolver
        : launch.mode === "billingless"
          ? passThroughResolver
          : zodResolver(formSchema),
    mode: "all",
    reValidateMode: "onChange",
    // Empty default values
    defaultValues: {
      rejectAsyncVisit: {
        cancelReasonCode: "",
        cancelReasonText: "",
      },
      dxBillingCodes: [],
      selectedDiagnosisIds: undefined,
      icd10: {
        childEncounters: [],
        primaryIcd10Code: {
          code: "",
          system: "",
          display: "",
          version: "",
        },
      },
      encounterTimeData: {
        chartingTimeSpent: 0,
        othersChartingTimeSpent: 0,
        avCallTimeSpent: 0,
        postEncounterActivities: [],
        patientType: null,
        billType: "duration",
        visitType: "av",
      },
      cptCode: {
        coding: [],
        text: "",
      },
      programEnrollment: { enrollmentApproved: null, encounterProgram },
    },
    // Synchronize values with network state from the backend
    values: {
      rejectAsyncVisit: {
        cancelReasonCode: "",
        cancelReasonText: "",
      },
      dxBillingCodes: [],
      selectedDiagnosisIds: undefined,
      icd10: {
        childEncounters: encounterCodingData.childEncounters.map(
          (childEncounter) => {
            return {
              episodeOfCareId: childEncounter.episodeOfCareId,
              episodeOfCareTitle: childEncounter.displayName,
              encounterId: childEncounter.id,
              conditionCodeValue: childEncounter.conditionCodeValue,
              icd10Codes:
                childEncounter.icd10Codes.length > 0
                  ? childEncounter.icd10Codes.map((code) => code.value)
                  : [{ code: "", system: "", display: "", version: "" }],
            };
          },
        ),
        primaryIcd10Code: {
          code: "",
          system: "",
          display: "",
          version: "",
        },
      },
      encounterTimeData: {
        chartingTimeSpent:
          encounterCodingData.encounterTimeDetails.chartingTimeSpent,
        othersChartingTimeSpent:
          encounterCodingData.encounterTimeDetails.othersChartingTimeSpent,
        avCallTimeSpent:
          encounterCodingData.encounterTimeDetails.avCallTimeSpent,
        postEncounterActivities:
          encounterCodingData.encounterTimeDetails.postEncounterActivities,
        patientType: null,
        billType: encounterCodingData.encounterTimeDetails.billType,
        visitType: "av",
      },
      cptCode: {
        coding: [],
        text: "",
      },
      programEnrollment: { enrollmentApproved: null, encounterProgram },
    },
    resetOptions: { keepDirtyValues: true },
  });

  const handleVisitClosureSubmit = (event: FormEvent<HTMLFormElement>) => {
    if (
      steps[step] !== "billingSummary" &&
      steps[step] !== "rejectAsyncVisit" &&
      steps[step] !== "carePlanPreview"
    ) {
      event.preventDefault();
      return;
    }

    void form.handleSubmit(onSubmit)(event);
  };

  return (
    <form onSubmit={handleVisitClosureSubmit}>
      <div className="flex flex-col gap-2">
        <div className="overflow-y-auto h-[50vh]">
          {currentStep === "diagnosisCharting" && (
            <DiagnosisCharting
              encounterId={encounterId}
              form={form}
              onLoadingChange={setIsDiagnosisLoading}
              onSelectableCountChange={setSelectableDiagnosisCount}
              patientId={patientId}
            />
          )}
          {currentStep === "dxBillingCodes" && (
            <DxBillingCodesStep
              form={form}
              patientId={patientId}
              visitDiagnoses={encounterCodingData.visitDiagnoses ?? []}
            />
          )}
          {currentStep === "billingCodes" && (
            <BillingCodesStep patientId={patientId} form={form} />
          )}
          {currentStep === "programEnrollment" && encounterProgram && (
            <ProgramEnrollmentStep
              form={form}
              encounterProgram={encounterProgram}
            />
          )}
          {currentStep === "visitTime" && <VisitDurationStep form={form} />}
          {currentStep === "cptCodes" && (
            <CPTCodesStep patientId={patientId} form={form} />
          )}
          {currentStep === "billingSummary" && (
            <BillingSummaryStep form={form} />
          )}
          {currentStep === "rejectAsyncVisit" && (
            <RequiresLiveVisitStep form={form} />
          )}
          {currentStep === "carePlanPreview" &&
            launch.mode === "billingless" && (
              <CarePlanPreviewStep
                carePlanIds={launch.carePlanIds}
                onReadyChange={setIsCarePlanPreviewReady}
              />
            )}
        </div>
        <VisitClosureFooter
          form={form}
          steps={steps}
          step={step}
          setStep={setStep}
          isSubmitting={isSubmitting}
          isDiagnosisLoading={isDiagnosisLoading}
          selectableDiagnosisCount={selectableDiagnosisCount}
          isCarePlanPreviewReady={isCarePlanPreviewReady}
        />
      </div>
    </form>
  );
};
